import React, { useCallback, useEffect, useState } from 'react';
import { AutosaveStatus } from '@components/AutosaveStatus';
import { useSubjectLanguageVariants } from '@features/user';
import { useAutoSave } from '@hooks/useAutoSave';
import CloseIcon from '@mui/icons-material/CloseOutlined';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';

import { useUpdatePatientModuleMutation } from '../store/api';
import type { PatientModuleInfo } from '../types';

import type { Allergy } from './PatientAllergiesCard';
import { PatientAllergiesEditCard } from './PatientAllergiesEditCard';

interface PatientAllergiesEditModalProps {
  open: boolean;
  onClose: () => void;
  patientId: string;
  allergies: Allergy[];
  moduleInfo: PatientModuleInfo;
  onAllergiesUpdated?: () => void;
  /** Source identifier for analytics */
  source: string;
}

export const PatientAllergiesEditModal: React.FC<PatientAllergiesEditModalProps> = ({
  open,
  onClose,
  patientId,
  allergies: initialAllergies,
  moduleInfo,
  onAllergiesUpdated,
  source,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { singularCap } = useSubjectLanguageVariants();
  const { patientContextBar: contextBarTracking } = useEventTracking();
  const [allergies, setAllergies] = useState<Allergy[]>(initialAllergies);
  const [hasLocalChanges, setHasLocalChanges] = useState(false);

  const [updateModule, { isLoading }] = useUpdatePatientModuleMutation();

  // Reset allergies when modal opens/closes or initial allergies change
  useEffect(() => {
    if (open) {
      setAllergies(initialAllergies);
      setHasLocalChanges(false);
    }
  }, [open, initialAllergies]);

  const performSave = useCallback(
    async (allergiesToSave: Allergy[]) => {
      try {
        // Transform allergies back to the format expected by the API
        const transformedData = allergiesToSave.map(allergy => {
          const name = allergy.allergy_name || allergy.name || '';
          return {
            name,
            allergy_name: name,
            reaction: allergy.reaction ?? null,
            severity: allergy.severity ?? null,
            start_date: allergy.start_date ?? null,
            end_date: allergy.end_date ?? null,
            active: allergy.isActive ?? true,
          };
        });

        await updateModule({
          patientId,
          patientModuleId: moduleInfo.patient_module_id,
          dataKey: moduleInfo.dataKey,
          data: transformedData,
        }).unwrap();
        setHasLocalChanges(false);
        onAllergiesUpdated?.();
      } catch (error) {
        console.error('Failed to save allergies:', error);
        contextBarTracking.saveFailed({
          patient_id: patientId,
          data_type: 'allergies',
          error_message: error instanceof Error ? error.message : 'Unknown error',
          source,
        });
        throw error;
      }
    },
    [patientId, moduleInfo, updateModule, onAllergiesUpdated, contextBarTracking, source],
  );

  const { triggerAutoSave, saveImmediately, status, lastSavedAt } = useAutoSave({
    onSave: performSave,
    debounceMs: 3000,
    enabled: open && hasLocalChanges,
    flushOnUnmount: true, // Safety net for unexpected unmounts
  });

  const handleAllergiesChange = useCallback(
    (newAllergies: Allergy[]) => {
      setAllergies(newAllergies);
      setHasLocalChanges(true);
      triggerAutoSave(newAllergies);
    },
    [triggerAutoSave],
  );

  // Handle modal close with flush of pending saves
  const handleClose = useCallback(async () => {
    contextBarTracking.editModalClosed({
      patient_id: patientId,
      data_type: 'allergies',
      had_changes: hasLocalChanges,
      source,
    });
    // If there are unsaved changes, flush immediately before closing
    if (hasLocalChanges && allergies.length >= 0) {
      await saveImmediately(allergies);
    }
    onClose();
  }, [hasLocalChanges, allergies, saveImmediately, onClose, patientId, source, contextBarTracking]);

  return (
    <Dialog
      open={open}
      onClose={() => {
        // Prevent closing during active save operation
        if (status === 'saving') {
          return;
        }
        handleClose();
      }}
      fullScreen={fullScreen}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: fullScreen ? '100%' : '80vh',
          maxHeight: fullScreen ? '100%' : '80vh',
          bgcolor: 'background.paper',
          boxShadow: 'none',
          border: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {`Edit ${singularCap} Allergies`}
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            disabled={status === 'saving'}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Box sx={{ flex: 1, overflow: 'auto', px: 2, pt: 2 }}>
          <PatientAllergiesEditCard
            allergies={allergies}
            onAllergiesChange={handleAllergiesChange}
            isLoading={isLoading}
            patientId={patientId}
            source={source}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <AutosaveStatus
          status={status}
          isDirty={hasLocalChanges}
          lastSavedAt={lastSavedAt}
          verbose
        />
        <Box sx={{ flexGrow: 1 }} />
        <Button
          onClick={handleClose}
          variant="contained"
          disabled={status === 'saving'}
          sx={{
            borderRadius: '999px',
            textTransform: 'none',
            fontWeight: 500,
            px: 3,
            bgcolor: 'primary.main',
            '&:hover': {
              bgcolor: 'primary.dark',
            },
          }}
        >
          {status === 'saving' ? 'Saving...' : 'Done'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
